
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Procurement } from '@/types/procurement';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FileText, ChevronDown, PlusCircle } from 'lucide-react';

// Type mapping for display purposes
const typeLabels: Record<string, string> = {
  construction: 'Bygg & Anläggning',
  it: 'IT & Digitalisering',
  services: 'Tjänster',
  supplies: 'Varor & Utrustning',
  healthcare: 'Vård & Omsorg',
  consulting: 'Konsulttjänster',
  other: 'Övrigt',
};

type ProcurementTableProps = {
  procurements: Procurement[];
  onDelete: (id: string) => Promise<void>;
  searchApplied: boolean;
};

const ProcurementTable = ({ procurements, onDelete, searchApplied }: ProcurementTableProps) => {
  const navigate = useNavigate();
  
  if (procurements.length === 0) {
    return (
      <div className="text-center py-10">
        <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">Inga upphandlingar hittades</h3>
        <p className="mt-1 text-gray-500">
          {searchApplied 
            ? "Prova att ändra filtreringen eller söktermer" 
            : "Skapa en ny upphandling för att komma igång"}
        </p>
        <Button 
          onClick={() => navigate('/new-procurement')}
          className="mt-4"
        >
          <PlusCircle size={16} className="mr-2" />
          Ny upphandling
        </Button>
      </div>
    );
  }
  
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[300px]">Upphandling</TableHead>
            <TableHead>Beställare</TableHead>
            <TableHead>Typ</TableHead>
            <TableHead className="hidden md:table-cell">Budget</TableHead>
            <TableHead className="hidden md:table-cell">Deadline</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Åtgärder</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {procurements.map((procurement) => (
            <TableRow key={procurement.id}>
              <TableCell className="font-medium">
                <div className="flex items-center">
                  <FileText className="h-4 w-4 mr-2 text-gray-400" />
                  {procurement.title}
                </div>
              </TableCell>
              <TableCell>{procurement.authority}</TableCell>
              <TableCell>{typeLabels[procurement.procurement_type] || procurement.procurement_type}</TableCell>
              <TableCell className="hidden md:table-cell">{procurement.budget || '-'}</TableCell>
              <TableCell className="hidden md:table-cell">
                {procurement.deadline 
                  ? new Date(procurement.deadline).toLocaleDateString('sv-SE') 
                  : '-'
                }
              </TableCell>
              <TableCell>
                <span
                  className={`px-2 py-1 rounded-full text-xs ${
                    procurement.status === 'Analyserad'
                      ? 'bg-green-100 text-green-800'
                      : procurement.status === 'Pågående'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {procurement.status}
                </span>
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <span className="sr-only">Öppna meny</span>
                      <ChevronDown size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate(`/analyses/${procurement.id}`)}>
                      Visa analys
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/procurements/${procurement.id}/edit`)}>
                      Redigera
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={() => onDelete(procurement.id)}
                    >
                      Ta bort
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ProcurementTable;
